/**
 * UniversalSearch — section-scoped by default, global toggle available.
 *
 * Behavior:
 *  - Type → Cmd-K-style popover with results grouped by source.
 *  - When section-scoped, only the active section's collection is consulted
 *    (e.g. on /admin/dossiers/* we query submissions/dossiers/sources).
 *  - When Global toggle is ON, we query across submissions, dossiers,
 *    members, audit log, and security events in parallel.
 *  - Keyboard: ⌘K / Ctrl-K to focus, Esc to close, ↑/↓ + Enter to navigate.
 *
 *  All search hits existing public-by-admin endpoints — there is no new
 *  global-search backend route yet, so we federate client-side.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Hash, FileText, User, Shield, History } from "lucide-react";
import {
  adminListLeads, adminAuditLog,
  adminSecurityFailedAccess, adminSecurityListPermanentBlocks,
} from "../../lib/api";

const sourceMeta = {
  submissions: { icon: FileText, color: "#CBB892", route: (id) => `/admin/dossiers/submissions?focus=${id}` },
  audit:       { icon: History,  color: "#9FA8D4", route: () => "/admin/security/audit" },
  security:    { icon: Shield,   color: "#D92D20", route: () => "/admin/security/failed-access" },
  blocks:      { icon: Shield,   color: "#D92D20", route: () => "/admin/security/failed-access" },
  members:     { icon: User,     color: "#5DD4B3", route: () => "/admin/members/audience" },
};

const sectionToSources = (s) => {
  switch (s) {
    case "dossiers": return ["submissions"];
    case "security": return ["security", "blocks", "audit"];
    case "members":  return ["members", "audit"];
    case "settings": return ["audit"];
    case "command":  return ["submissions", "security", "audit"];
    default:         return ["submissions"];
  }
};

const GLOBAL_SOURCES = ["submissions", "security", "blocks", "audit"];

export const UniversalSearch = ({ activeSection, opToken, userToken }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [global, setGlobal] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // ⌘K / Ctrl-K focus
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Debounced query
  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => runSearch(), 220);
    return () => clearTimeout(timer);
  }, [q, global, activeSection]);

  const sourcesToQuery = useMemo(
    () => (global ? GLOBAL_SOURCES : sectionToSources(activeSection)),
    [global, activeSection],
  );

  const runSearch = async () => {
    const term = q.trim().toLowerCase();
    if (!term) return;
    setLoading(true);
    const out = [];
    const tasks = sourcesToQuery.map(async (src) => {
      try {
        if (src === "submissions") {
          const data = await adminListLeads(opToken, {}, userToken);
          const list = data?.leads || (Array.isArray(data) ? data : []);
          const hits = list.filter((s) => {
            const blob = [
              s.title, s.topic, s.rationale, s.summary,
              s.category, s.submitter, s.requester_email, s.workflow_status,
              ...(s.spam_flags || []),
            ].filter(Boolean).join(" ").toLowerCase();
            return blob.includes(term);
          }).slice(0, 6);
          hits.forEach((s) =>
            out.push({
              source: "submissions",
              id: s.id,
              title: s.title || s.topic || "(untitled)",
              subtitle: `${s.workflow_status || s.status || ""} · ${s.category || ""}`,
              raw: s,
            }),
          );
        } else if (src === "security") {
          const r = await adminSecurityFailedAccess(opToken, { search: term, limit: 5 }, userToken);
          (r.attempts || []).forEach((a) =>
            out.push({
              source: "security",
              id: `${a.timestamp}-${a.ip_address}`,
              title: `${a.failure_reason || "denied"} · ${a.ip_address || "?"}`,
              subtitle: `${a.country || "?"} · ${a.user_agent?.slice(0, 50) || ""}`,
              raw: a,
            }),
          );
        } else if (src === "blocks") {
          const r = await adminSecurityListPermanentBlocks(opToken, { limit: 10 }, userToken);
          (r.blocks || []).filter((b) => {
            return (b.value || "").toLowerCase().includes(term) ||
                   (b.reason || "").toLowerCase().includes(term);
          }).slice(0, 4).forEach((b) =>
            out.push({
              source: "blocks",
              id: `${b.kind}:${b.value}`,
              title: `${b.kind.toUpperCase()} block · ${b.kind === "ip" ? b.value : (b.value || "").slice(0, 18) + "…"}`,
              subtitle: b.reason || b.blocked_by || "—",
              raw: b,
            }),
          );
        } else if (src === "audit") {
          const r = await adminAuditLog(opToken, userToken, 50);
          (r?.events || r || []).filter((ev) => {
            const blob = [ev.event, ev.actor_email, ev.target, JSON.stringify(ev.metadata || {})]
              .filter(Boolean).join(" ").toLowerCase();
            return blob.includes(term);
          }).slice(0, 6).forEach((ev) =>
            out.push({
              source: "audit",
              id: ev.id || `${ev.timestamp}-${ev.event}`,
              title: ev.event,
              subtitle: `${ev.actor_email || "system"} · ${ev.target || ""}`,
              raw: ev,
            }),
          );
        }
      } catch {
        // silent — federation must degrade gracefully
      }
    });
    await Promise.all(tasks);
    setResults(out);
    setCursor(0);
    setLoading(false);
    setOpen(true);
  };

  const onPick = (r) => {
    const meta = sourceMeta[r.source];
    if (!meta) return;
    setOpen(false);
    setQ("");
    navigate(meta.route(r.id));
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      onPick(results[cursor]);
    }
  };

  // Group results by source for display
  const grouped = useMemo(() => {
    const g = {};
    results.forEach((r) => {
      (g[r.source] = g[r.source] || []).push(r);
    });
    return g;
  }, [results]);

  return (
    <div className="relative w-full" data-testid="admin-universal-search">
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#757065]" />
        <input
          ref={inputRef}
          data-testid="admin-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKey}
          placeholder={`Search ${global ? "everywhere" : activeSection || "admin"}…  ⌘K`}
          className="w-full pl-8 pr-24 py-2 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none text-[#E4DFD3] font-mono text-xs placeholder-[#454038]"
        />
        <label
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] cursor-pointer"
        >
          <input
            data-testid="admin-search-global-toggle"
            type="checkbox"
            checked={global}
            onChange={(e) => setGlobal(e.target.checked)}
            className="accent-[#D92D20] h-3 w-3"
          />
          Global
        </label>
      </div>

      {open && q && (
        <div
          data-testid="admin-search-popover"
          className="absolute left-0 right-0 mt-1 z-50 max-h-[60vh] overflow-y-auto bg-[#0F0F0F] border border-[#D92D20]/40 shadow-2xl"
        >
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 font-mono text-[10px] text-[#A39E93]">
              <Loader2 size={11} className="animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-4 font-mono text-[10px] text-[#757065] italic">
              // No matches{global ? "" : " in this section"}.
            </div>
          )}
          {!loading && Object.entries(grouped).map(([src, items]) => {
            const meta = sourceMeta[src];
            const Icon = meta?.icon || Hash;
            return (
              <div key={src} className="border-b border-[#2D2A26] last:border-b-0">
                <div className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] bg-[#0a0907]">
                  {src}
                </div>
                {items.map((r) => {
                  const i = results.indexOf(r);
                  const active = i === cursor;
                  return (
                    <button
                      key={`${src}-${r.id}`}
                      data-testid={`admin-search-result-${i}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onPick(r)}
                      className={`w-full text-left px-3 py-2 flex items-start gap-2 border-l-2 ${
                        active
                          ? "bg-[#1a0808] border-[#D92D20]"
                          : "border-transparent hover:bg-[#1a0808]/40"
                      }`}
                    >
                      <Icon size={11} style={{ color: meta?.color }} className="mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-[#E4DFD3] truncate">
                          {r.title}
                        </div>
                        {r.subtitle && (
                          <div className="font-mono text-[9px] text-[#757065] truncate">
                            {r.subtitle}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UniversalSearch;
